import type { ComponentType, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { subTabPillClass, subTabPillTrackClass } from "./subTabPills";

export type PatientSubTabPillItem = {
  value: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
};

type Props = {
  tabs: PatientSubTabPillItem[];
  activeTab: string;
  onTabChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  /** When true, inactive tabs show icon only; active tab shows icon + label */
  labelOnlyWhenActive?: boolean;
  trailing?: ReactNode;
};

export function PatientSubTabPills({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
  className,
  labelOnlyWhenActive = false,
  trailing,
}: Props) {
  if (!tabs.length) return null;

  return (
    <div className={cn("bg-[#F3F4F6] px-4 py-2.5", className)}>
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className={subTabPillTrackClass} role="tablist" aria-label={ariaLabel}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={tab.label}
                  title={tab.label}
                  className={cn(
                    subTabPillClass(active),
                    labelOnlyWhenActive && !active && "px-3",
                  )}
                  onClick={() => onTabChange(tab.value)}
                >
                  {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                  {!labelOnlyWhenActive || active ? (
                    <span className="whitespace-nowrap">{tab.label}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
    </div>
  );
}